// src/layouts/applications/case-list/index.js
// Call It In LLC — AxisSync Active Cases

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../examples/Navbars/DashboardNavbar";
import MDSnackbar from "../../../components/MDSnackbar";
import { getDrivers } from "../../../data/drivers";
import { getBaseUrl, request } from "../../../api/client";

const API = getBaseUrl();
const CASE_DRIVERS = getDrivers();

const CASE_STATUSES = [
  "Pending Coordination",
  "Dispatched",
  "In Transit",
  "At Decon",
  "Delivered to SPD",
  "Paused",
  "Canceled",
  "Complete",
];

/* ─────────────────────── helpers ─────────────────────── */

const trunc = (s, n = 38) => (!s ? "—" : s.length > n ? s.slice(0, n - 1) + "…" : s);

/**
 * Extract the procedure summary line from a notes field that was written by
 * the Create Case form.  Returns null if no [PROC] block is present, so this
 * is safe to call on all existing records.
 * Format stored in notes: "[PROC] Left Hip – Primary | optional proc note"
 */
const parseProcedureSummary = (notesStr) => {
  if (!notesStr) return null;
  const match = String(notesStr).match(/^\[PROC\]\s*([^\n|]+)/);
  return match ? match[1].trim() : null;
};

const fmtDateTime = (date, time) => {
  if (!date) return "—";
  const d = new Date(`${date}T${time || "00:00"}`);
  return Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
        (time ? `  ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}` : "");
};

// AxisSync status color map
const STATUS_COLORS = {
  "pending coordination": "#F59E0B",
  dispatched:            "#3B82F6",
  "in transit":          "#6366F1",
  "at decon":            "#F97316",
  "delivered to spd":    "#10B981",
  complete:              "#059669",
};

const statusPill = (status, isEmergency = false) => {
  const color = isEmergency
    ? "#EF4444"
    : STATUS_COLORS[String(status).toLowerCase()] || "#9CA3AF";
  return {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    background: color,
    whiteSpace: "nowrap",
    letterSpacing: "0.02em",
    boxShadow: isEmergency ? "0 0 0 2px rgba(239,68,68,0.35)" : "none",
  };
};

const isEmergencyCase = (c) =>
  c.isEmergency === true ||
  String(c.status || "").toLowerCase().includes("emergency") ||
  String(c.notes || "").startsWith("STAT / TRAUMA");

/* ─────────────────────── layout styles ─────────────────────── */

const page   = { maxWidth: 1200, margin: "28px auto", padding: "0 16px" };
const hBar   = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 };
const titleS = { fontSize: 22, fontWeight: 800, margin: 0 };
const actRow = { display: "flex", gap: 8, alignItems: "center" };

const searchBox = {
  width: 260,
  height: 40,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  padding: "0 12px",
  fontSize: 14,
};

const card = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 8px 30px rgba(2,6,23,0.06)",
  padding: 16,
};

// 10 columns: Seq, Case#, Hospital, Surgeon, Date/Time, Trays, Destination, Driver, Status, Invoice
const COLS = "58px 130px 1fr 1fr 170px 70px 140px 165px 165px 110px";

const headRow = {
  display: "grid",
  gridTemplateColumns: COLS,
  gap: 12,
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
  borderBottom: "2px solid #f3f4f6",
};

const rowS = {
  display: "grid",
  gridTemplateColumns: COLS,
  gap: 12,
  alignItems: "center",
  padding: "13px 12px",
  borderRadius: 10,
};

const zebra = (i) => ({ background: i % 2 ? "#fafbfc" : "#ffffff" });

const emergencyRow = {
  background: "rgba(239,68,68,0.06)",
  outline: "1px solid rgba(239,68,68,0.25)",
};

const cell = { fontSize: 12, color: "#374151" };
const mono = { fontVariantNumeric: "tabular-nums" };
const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const thBtn = {
  background: "transparent",
  border: 0,
  padding: 0,
  fontSize: 11,
  fontWeight: 800,
  color: "#6b7280",
  cursor: "pointer",
  textAlign: "left",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
};

const btn = {
  border: "1px solid #E5E7EB",
  background: "#fff",
  borderRadius: 8,
  padding: "6px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontWeight: 600,
};

const btnQB = {
  border: "1.5px solid #2CA01C",
  background: "#fff",
  borderRadius: 8,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: "#2CA01C",
  cursor: "pointer",
  whiteSpace: "nowrap",
  width: "100%",
};

const btnAddTask = {
  border: "1.5px solid #0ea5e9",
  background: "#f0f9ff",
  borderRadius: 8,
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 700,
  color: "#0369a1",
  cursor: "pointer",
  whiteSpace: "nowrap",
  width: "100%",
};

/* ─────────────────────── sorters ─────────────────────── */

const SORTERS = {
  caseNumber:  (a, b) => String(a.caseNumber  || "").localeCompare(String(b.caseNumber  || "")),
  hospitalName:(a, b) => String(a.hospitalName|| "").localeCompare(String(b.hospitalName|| "")),
  surgeonName: (a, b) => String(a.surgeonName || "").localeCompare(String(b.surgeonName || "")),
  surgeryDate: (a, b) => String(a.surgeryDate || "").localeCompare(String(b.surgeryDate || "")),
  status:      (a, b) => String(a.status      || "").localeCompare(String(b.status      || "")),
};

/* ─────────────────────── component ─────────────────────── */

export default function CaseList() {
  const navigate = useNavigate();
  const [cases, setCases]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]           = useState("");
  const [sort, setSort]     = useState({ key: "surgeryDate", dir: "asc" });

  const [errorSB, setErrorSB]   = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [invoicingCaseId, setInvoicingCaseId] = useState(null);
  const closeErrorSB = () => setErrorSB(false);
  const closeSuccessSB = () => setSuccessSB(false);

  // Fetch all cases from AxisSync backend
  useEffect(() => {
    const load = async () => {
      try {
        const res = await request("/cases/");
        if (!res.ok) throw new Error(`Server responded with ${res.status}`);
        const data = await res.json();
        setCases(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn("AxisSync backend unreachable:", err);
        setErrorMsg("Could not reach backend. Verify the server is running on port 8000.");
        setErrorSB(true);
        setCases([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Search filter
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return cases;
    return cases.filter((c) => {
      const procSummary = parseProcedureSummary(c.notes) || "";
      return [c.caseNumber, c.hospitalName, c.surgeonName, c.vendorRep, c.status, procSummary]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [cases, q]);

  // Sort
  const rows = useMemo(() => {
    const sorter = SORTERS[sort.key] || SORTERS.surgeryDate;
    const arr = [...filtered].sort(sorter);
    return sort.dir === "desc" ? arr.reverse() : arr;
  }, [filtered, sort]);

  const clickSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  const arrow = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  const updateCaseStatus = async (caseObj, newStatus) => {
    try {
      const res = await request(`/cases/${caseObj.id}/status`, {
        method: "PUT",
        body: { status: newStatus },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setCases((prev) =>
        prev.map((c) => (c.id === caseObj.id ? { ...c, status: newStatus } : c))
      );
      const lower = newStatus.toLowerCase();
      if (lower === "paused" || lower === "canceled") {
        setSuccessMsg(`Case ${newStatus} — linked task cascaded to driver board.`);
      } else {
        setSuccessMsg(`Status → "${newStatus}"`);
      }
      setSuccessSB(true);
    } catch (err) {
      setErrorMsg(`Status update failed: ${err.message}`);
      setErrorSB(true);
    }
  };

  const updateCaseDriver = async (caseObj, newDriver) => {
    try {
      // Use the general PATCH route — it cascades assignedDriver to ALL linked
      // tasks via both linkedCaseId scan and dispatched_task_id fallback.
      const res = await request(`/cases/${caseObj.id}`, {
        method: "PUT",
        body: { assignedDriver: newDriver },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setCases((prev) =>
        prev.map((c) => (c.id === caseObj.id ? { ...c, assignedDriver: newDriver } : c))
      );
      setSuccessMsg(`Driver assigned: ${newDriver || "Unassigned"}`);
      setSuccessSB(true);
    } catch (err) {
      setErrorMsg(`Driver reassignment failed: ${err.message}`);
      setErrorSB(true);
    }
  };

  // Update the sequenceOrder on the auto-dispatched task linked to this case.
  // Uses dispatched_task_id stored on the case record at creation time.
  const updateCaseSequence = async (caseObj, newOrder) => {
    const parsed = parseInt(newOrder, 10);
    if (Number.isNaN(parsed) || parsed < 1) return;
    const taskId = caseObj.dispatched_task_id;
    if (!taskId) return; // case pre-dates this feature
    try {
      const res = await request(`/tasks/${taskId}/sequence`, {
        method: "PUT",
        body: { sequenceOrder: parsed },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      // Reflect change locally so UI stays in sync
      setCases((prev) =>
        prev.map((c) =>
          c.id === caseObj.id ? { ...c, _taskSequenceOrder: parsed } : c
        )
      );
    } catch (err) {
      setErrorMsg(`Sequence update failed: ${err.message}`);
      setErrorSB(true);
    }
  };

  const generateCaseInvoice = async (caseObj) => {
    setInvoicingCaseId(caseObj.id);
    try {
      const res = await request(`/cases/${caseObj.id}/invoice`, { method: "POST" });
      const result = await res.json();
      if (res.ok) {
        const total = result.grand_total != null ? ` ($${Number(result.grand_total).toFixed(2)})` : "";
        const tasks = result.tasks_aggregated != null ? `, ${result.tasks_aggregated} task(s) rolled up` : "";
        setSuccessMsg(
          `QB Invoice #${result.quickbooks_invoice_id} created${total}${tasks}.`
        );
        setSuccessSB(true);
        // Reflect qbInvoiceId on local case state for button label update
        setCases((prev) =>
          prev.map((c) =>
            c.id === caseObj.id ? { ...c, qbInvoiceId: result.quickbooks_invoice_id } : c
          )
        );
      } else {
        setErrorMsg(result.detail || "QuickBooks invoice creation failed.");
        setErrorSB(true);
      }
    } catch (err) {
      setErrorMsg("Network error during QBO sync.");
      setErrorSB(true);
    } finally {
      setInvoicingCaseId(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div style={page}>
        {/* ── Header bar ── */}
        <div style={hBar}>
          <h2 style={titleS}>Active Cases</h2>
          <div style={actRow}>
            <input
              placeholder="Search cases…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={searchBox}
            />
            <button
              style={btn}
              onClick={() => { window.location.hash = "/applications/case-create"; }}
            >
              + New Case
            </button>
          </div>
        </div>

        {/* ── Table card ── */}
        <div style={card}>
          {loading ? (
            <div style={{ textAlign: "center", padding: 48, color: "#9CA3AF", fontSize: 14 }}>
              Loading cases…
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1000 }}>

                {/* Column headers */}
                <div style={headRow}>
                  <div style={{ ...thBtn, cursor: "default" }} title="Route sequence for the linked dispatch task">Seq</div>
                  <button style={thBtn} onClick={() => clickSort("caseNumber")}>
                    Case / PO{arrow("caseNumber")}
                  </button>
                  <button style={thBtn} onClick={() => clickSort("hospitalName")}>
                    Hospital{arrow("hospitalName")}
                  </button>
                  <button style={thBtn} onClick={() => clickSort("surgeonName")}>
                    Surgeon{arrow("surgeonName")}
                  </button>
                  <button style={thBtn} onClick={() => clickSort("surgeryDate")}>
                    Date &amp; Time{arrow("surgeryDate")}
                  </button>
                  <div style={thBtn}>Trays</div>
                  <div style={thBtn}>Tray Dest.</div>
                  <div style={thBtn}>Driver</div>
                  <button style={thBtn} onClick={() => clickSort("status")}>
                    Status{arrow("status")}
                  </button>
                  <div style={thBtn}>Invoice</div>
                </div>

                {/* Rows */}
                {rows.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 56, color: "#9CA3AF" }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🏥</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>No active cases found.</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Create a new surgical case to get started.
                    </div>
                  </div>
                ) : (
                  rows.map((c, i) => {
                    const emerg = isEmergencyCase(c);
                    return (
                    <div key={c.id || i} style={{ ...rowS, ...(emerg ? emergencyRow : zebra(i)) }}>

                      {/* Route Sequence — controls the linked dispatched task */}
                      <div>
                        {c.dispatched_task_id ? (
                          <input
                            type="number"
                            min={1}
                            defaultValue={c._taskSequenceOrder ?? 1}
                            key={`cseq-${c.id}-${c._taskSequenceOrder}`}
                            onBlur={(e) => updateCaseSequence(c, e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") updateCaseSequence(c, e.target.value); }}
                            style={{
                              width: 48,
                              textAlign: "center",
                              border: "1px solid #E5E7EB",
                              borderRadius: 6,
                              padding: "4px 4px",
                              fontSize: 12,
                              fontWeight: 700,
                              color: (c._taskSequenceOrder ?? 1) === 1 ? "#10B981" : "#374151",
                              background: (c._taskSequenceOrder ?? 1) === 1 ? "#ECFDF5" : "#fff",
                            }}
                            title="Route stop order for driver (1 = next stop)"
                          />
                        ) : (
                          <span style={{ color: "#D1D5DB", fontSize: 11 }}>—</span>
                        )}
                      </div>

                      {/* Case / PO Number */}
                      <div style={{ ...cell, ...mono, ...ellipsis, fontWeight: 700 }}
                           title={c.caseNumber || "—"}>
                        {c.caseNumber || <span style={{ color: "#D1D5DB" }}>—</span>}
                      </div>

                      {/* Hospital + procedure summary */}
                      <div style={{ ...cell, ...ellipsis }} title={c.hospitalName || ""}>
                        <div>{trunc(c.hospitalName, 36)}</div>
                        {parseProcedureSummary(c.notes) && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: "#6366F1", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {parseProcedureSummary(c.notes)}
                          </div>
                        )}
                      </div>

                      {/* Surgeon */}
                      <div style={{ ...cell, ...ellipsis }} title={c.surgeonName || "—"}>
                        {trunc(c.surgeonName, 30) || <span style={{ color: "#D1D5DB" }}>—</span>}
                      </div>

                      {/* Date & Time */}
                      <div style={{ ...cell, ...mono, fontSize: 11 }}>
                        {fmtDateTime(c.surgeryDate, c.surgeryTime)}
                      </div>

                      {/* Tray count */}
                      <div style={{ ...cell, ...mono, textAlign: "center", fontWeight: 700 }}>
                        {c.trayCount ?? "—"}
                      </div>

                      {/* Tray destination */}
                      <div style={{ ...cell, fontSize: 11, ...ellipsis }}
                           title={c.trayDestination || ""}>
                        {c.trayDestination || "—"}
                      </div>

                      {/* Driver — inline reassignment dropdown */}
                      <div>
                        <select
                          value={c.assignedDriver || ""}
                          onChange={(e) => updateCaseDriver(c, e.target.value)}
                          style={{
                            width: "100%",
                            padding: "4px 6px",
                            borderRadius: 6,
                            border: "1px solid #E5E7EB",
                            background: "#fff",
                            color: c.assignedDriver ? "#374151" : "#9CA3AF",
                            fontSize: 11,
                            fontWeight: c.assignedDriver ? 600 : 400,
                            cursor: "pointer",
                            outline: "none",
                          }}
                        >
                          <option value="">— Unassigned —</option>
                          {CASE_DRIVERS.map((d) => (
                            <option key={d.id} value={d.name}>{d.name} · {d.vehicle}</option>
                          ))}
                        </select>
                      </div>

                      {/* Status — interactive dropdown */}
                      <div>
                        {(() => {
                          const currentStatus = c.status || "Pending Coordination";
                          const sColor = isEmergencyCase(c)
                            ? "#EF4444"
                            : STATUS_COLORS[currentStatus.toLowerCase()] || "#9CA3AF";
                          return (
                            <select
                              value={currentStatus}
                              onChange={(e) => updateCaseStatus(c, e.target.value)}
                              style={{
                                width: "100%",
                                padding: "4px 6px",
                                borderRadius: 6,
                                border: `1.5px solid ${sColor}`,
                                background: "#fff",
                                color: sColor,
                                fontWeight: 700,
                                fontSize: 11,
                                cursor: "pointer",
                                outline: "none",
                              }}
                            >
                              {CASE_STATUSES.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          );
                        })()}
                      </div>

                      {/* Actions — Add Task + QB Invoice */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <button
                          style={btnAddTask}
                          onClick={() =>
                            navigate("/applications/task-create", {
                              state: {
                                caseId:  c.id,
                                pickup:  c.vendorAddress  || "",
                                dropoff: c.hospitalAddress || "",
                                client:  c.surgeonName    || c.vendorRep || "",
                              },
                            })
                          }
                        >
                          + Add Task
                        </button>

                        {c.qbInvoiceId ? (
                          <span
                            title={`QBO Invoice #${c.qbInvoiceId}`}
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: "#2CA01C",
                              background: "#F0FFF0",
                              border: "1px solid #C6F6D5",
                              borderRadius: 6,
                              padding: "4px 6px",
                              display: "inline-block",
                              whiteSpace: "nowrap",
                              textAlign: "center",
                            }}
                          >
                            ✓ #{c.qbInvoiceId}
                          </span>
                        ) : (
                          <button
                            style={{
                              ...btnQB,
                              opacity: invoicingCaseId === c.id ? 0.6 : 1,
                              cursor: invoicingCaseId === c.id ? "wait" : "pointer",
                            }}
                            disabled={invoicingCaseId === c.id}
                            onClick={() => generateCaseInvoice(c)}
                          >
                            {invoicingCaseId === c.id ? "Syncing…" : "QB Invoice"}
                          </button>
                        )}
                      </div>

                    </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Row count footer */}
          {!loading && rows.length > 0 && (
            <div style={{ textAlign: "right", padding: "10px 12px 2px", fontSize: 11, color: "#9CA3AF" }}>
              {rows.length} case{rows.length !== 1 ? "s" : ""}
              {q ? ` matching "${q}"` : ""}
            </div>
          )}
        </div>
      </div>

      {/* Success snackbar */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Updated"
        content={successMsg}
        dateTime={new Date().toLocaleString()}
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      {/* Error snackbar */}
      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={errorMsg}
        dateTime={new Date().toLocaleString()}
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}
